import { Alert, Button, Col, Form, InputGroup, Modal, ModalHeader, ModalTitle, Row, Table } from "react-bootstrap";
import { AutoCol, Icon, RadioGroup, RadioOption } from "../../../component-library";
import { capitalizeFirst, displayTimeZonelessDateAndTime, getValueOrBlank } from "../../../helper-functions";
import { useCurrentCase, useSelectOptionsFromSlug } from "../../../hooks";
import { DaughterTissue, SelectOption, TissueTypes } from "../../../models";
import { YesNoRadioOptions } from "../../../models/form/RadioOptions";
import { RenderedFormProps } from "../RenderForm";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { RecoveryFormData } from ".";

const tissueTypeOptions = [
    TissueTypes.Aqueous,
    TissueTypes.Vitreous,
    TissueTypes.Pole,
    TissueTypes.Conjunctiva,
].map<SelectOption>((o) => ({ value: o, label: o }));

const yesNoOptions = YesNoRadioOptions.map<RadioOption>((o) => ({ ...o, value: capitalizeFirst(o.value) })); //yes/no values from Recovery data are capitalized

const volumeUnitOptions = ["µL", "mL"].map<SelectOption>((o) => ({ value: o, label: o }));

export function DaughterTissuesList({ formState }: Readonly<RenderedFormProps>) {
    const [showDaughterTissueModal, setShowDaughterTissueModal] = useState(false);
    const [daughterTissueType, setDaughterTissueType] = useState<string>(tissueTypeOptions[0].label);
    const [daughterTissueOs, setDaughterTissueOs] = useState<boolean>(false);
    const [daughterTissueOd, setDaughterTissueOd] = useState<boolean>(false);
    const [daughterTissueVolOs, setDaughterTissueVolOs] = useState(0);
    const [daughterTissueVolOd, setDaughterTissueVolOd] = useState(0);
    const [daughterTissueVolUnitOs, setDaughterTissueVolUnitOs] = useState("mL");
    const [daughterTissueVolUnitOd, setDaughterTissueVolUnitOd] = useState("mL");
    const [currentDaughterTissueIndex, setCurrentDaughterTissueIndex] = useState<string | undefined>(undefined);
    const [isDuplicateSample, setIsDuplicateSample] = useState(false);
    const { isReadonly } = formState;
    const formData: RecoveryFormData = formState.formData?.data ?? {};
    const mostRecentSignature = formState.formData?.signatures?.sort((a, b) => {
        const dateA = new Date(a.createdOn!);
        const dateB = new Date(b.createdOn!);
        return dateB.getTime() - dateA.getTime();
    })[0];
    const { setValue } = useFormContext();
    const { case: _case } = useCurrentCase({ includeDetails: true });

    const preservationDate = displayTimeZonelessDateAndTime(formData.ProcedureDate, formData.ProcedureTime);
    const timezoneOptions = useSelectOptionsFromSlug("time_zone");
    const preservationTimezone = getValueOrBlank(
        timezoneOptions.find((x) => x.value === formData.ProcedureTimeZone)?.label
    );

    const createdDaughterTissues = _case?.tissues?.filter((t) => t.daughterTissueIndex !== null);

    const clearDaughterTissueFields = () => {
        setDaughterTissueType(tissueTypeOptions[0].label);
        setDaughterTissueOs(false);
        setDaughterTissueOd(false);
        setDaughterTissueVolOs(0);
        setDaughterTissueVolOd(0);
        setDaughterTissueVolUnitOs("mL");
        setDaughterTissueVolUnitOd("mL");
        setCurrentDaughterTissueIndex(undefined);
        setIsDuplicateSample(false);
    };

    const tissueCreated = useMemo(() => {
        return (
            currentDaughterTissueIndex !== undefined &&
            createdDaughterTissues?.some((t) => t.daughterTissueIndex === String(currentDaughterTissueIndex))
        );
    }, [createdDaughterTissues, currentDaughterTissueIndex]);

    const handleSaveDaughterTissue = () => {
        let index =
            currentDaughterTissueIndex ?? (formData.DaughterTissues?.length > 0 ? formData.DaughterTissues?.length : 0);

        if (daughterTissueOs) {
            const existingOsDt = formData.DaughterTissues?.find(
                (t: DaughterTissue) => t.daughterTissueType === daughterTissueType && t.daughterTissueOs
            );
            if (existingOsDt && currentDaughterTissueIndex === undefined) {
                setIsDuplicateSample(true);
                return;
            }
            setValue(`DaughterTissues.${index}.daughterTissueType`, daughterTissueType, { shouldDirty: true });
            setValue(`DaughterTissues.${index}.daughterTissueOs`, daughterTissueOs, { shouldDirty: true });
            setValue(`DaughterTissues.${index}.daughterTissueVolOs`, daughterTissueVolOs, { shouldDirty: true });
            setValue(`DaughterTissues.${index}.daughterTissueVolUnitOs`, daughterTissueVolUnitOs, {
                shouldDirty: true,
            });
            setValue(`DaughterTissues.${index}.daughterTissueIndex`, index, { shouldDirty: true });
            index++;
        }
        if (daughterTissueOd) {
            const existingOdDt = formData.DaughterTissues?.find(
                (t: DaughterTissue) => t.daughterTissueType === daughterTissueType && t.daughterTissueOd
            );
            if (existingOdDt && currentDaughterTissueIndex === undefined) {
                setIsDuplicateSample(true);
                return;
            }
            setValue(`DaughterTissues.${index}.daughterTissueType`, daughterTissueType, { shouldDirty: true });
            setValue(`DaughterTissues.${index}.daughterTissueOd`, daughterTissueOd, { shouldDirty: true });
            setValue(`DaughterTissues.${index}.daughterTissueVolOd`, daughterTissueVolOd, { shouldDirty: true });
            setValue(`DaughterTissues.${index}.daughterTissueVolUnitOd`, daughterTissueVolUnitOd, {
                shouldDirty: true,
            });
            setValue(`DaughterTissues.${index}.daughterTissueIndex`, index, { shouldDirty: true });
        }
        clearDaughterTissueFields();
        setShowDaughterTissueModal(false);
    };

    const handleCloseDaughterTissueModal = () => {
        clearDaughterTissueFields();
        setShowDaughterTissueModal(false);
    };

    const removeDaughterTissue = (index: string) => {
        const daughterTissues = formData.DaughterTissues?.filter(
            (t: DaughterTissue) => t.daughterTissueIndex !== index
        );
        setValue("DaughterTissues", daughterTissues, { shouldDirty: true });
        handleCloseDaughterTissueModal();
    };

    const handleDaughterTissueRowClick = (index: string) => {
        if (isReadonly) return;
        setCurrentDaughterTissueIndex(index);
        const tissue = formData?.DaughterTissues?.find((t: DaughterTissue) => t.daughterTissueIndex === index);
        setDaughterTissueType(tissue.daughterTissueType);
        if (tissue.daughterTissueOs) {
            setDaughterTissueOs(true);
            setDaughterTissueVolOs(tissue.daughterTissueVolOs);
            setDaughterTissueVolUnitOs(tissue.daughterTissueVolUnitOs);
        } else {
            setDaughterTissueOd(true);
            setDaughterTissueVolOd(tissue.daughterTissueVolOd);
            setDaughterTissueVolUnitOd(tissue.daughterTissueVolUnitOd);
        }
        setShowDaughterTissueModal(true);
    };

    return (
        <>
            <h3>
                <b>Daughter Tissues</b>
            </h3>
            <Row>
                <AutoCol>
                    <RadioGroup
                        inline
                        inlineGroupName
                        accessor="daughterTissue"
                        options={yesNoOptions}
                        groupName="Were daughter tissues recovered?"
                        onChange={(val) => setShowDaughterTissueModal(val === "Yes")}
                    />
                </AutoCol>
            </Row>
            <Row>
                <Col xxl="auto" className="mb-3">
                    <Table hover>
                        <tbody>
                            {formData?.DaughterTissues?.map((dt: DaughterTissue) => {
                                const side = dt.daughterTissueOd ? "OD" : "OS";
                                const vol = side === "OD" ? dt.daughterTissueVolOd : dt.daughterTissueVolOs;
                                const unit = side === "OD" ? dt.daughterTissueVolUnitOd : dt.daughterTissueVolUnitOs;
                                const tissueNumber =
                                    createdDaughterTissues?.find(
                                        (t) => t.daughterTissueIndex === String(dt.daughterTissueIndex)
                                    )?.number ?? "";
                                return (
                                    <tr
                                        key={dt.daughterTissueIndex}
                                        onClick={() => handleDaughterTissueRowClick(dt.daughterTissueIndex)}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <td>{tissueNumber}</td>
                                        <td>{dt.daughterTissueType}</td>
                                        <td>{side}</td>
                                        <td>
                                            {vol} {unit}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                    {formData.daughterTissue === "Yes" && (
                        <Button
                            size="sm"
                            variant="primary"
                            onClick={() => setShowDaughterTissueModal(true)}
                            title="Add Daughter Tissue"
                            aria-label="Add Daughter Tissue"
                        >
                            <Icon icon={fas.faPlus} className="me-1" /> Daughter Tissue
                        </Button>
                    )}
                </Col>
            </Row>

            <Modal show={showDaughterTissueModal} onHide={handleCloseDaughterTissueModal} centered size="lg">
                <ModalHeader closeButton>
                    <ModalTitle>Daughter Tissue</ModalTitle>
                </ModalHeader>
                <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                    <Row>
                        <Col xxl={6} className="mb-2">
                            <Form.Group controlId="tissueType">
                                <Form.Label>Tissue Type</Form.Label>
                                <Form.Select
                                    value={daughterTissueType}
                                    onChange={(e) => setDaughterTissueType(e.target.value)}
                                    aria-label="Tissue Type"
                                    disabled={tissueCreated}
                                >
                                    {tissueTypeOptions.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>
                    <Row>
                        <Col xxl={6} className="mb-2">
                            <Form.Check
                                className="mb-2"
                                label="OS"
                                id="daughterTissueOs"
                                type="checkbox"
                                checked={daughterTissueOs}
                                onChange={() => setDaughterTissueOs(!daughterTissueOs)}
                                disabled={currentDaughterTissueIndex !== undefined}
                            />

                            <Form.Group className="mb-2">
                                <Form.Label>Date/Time of Preservation:</Form.Label>
                                <Form.Control disabled value={`${preservationDate} ${preservationTimezone}`} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label>Procured By:</Form.Label>
                                <Form.Control disabled value={mostRecentSignature?.userName ?? ""} />
                            </Form.Group>
                            <Form.Group controlId="osVolumeUnit">
                                <Form.Label>Volume</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        value={daughterTissueVolOs}
                                        min={0}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const regex = /^\d+(\.\d{0,2})?$/;
                                            if (regex.test(value)) {
                                                setDaughterTissueVolOs(+value);
                                            }
                                        }}
                                        disabled={currentDaughterTissueIndex !== undefined && !daughterTissueOs}
                                    />
                                    <Form.Select
                                        value={daughterTissueVolUnitOs}
                                        onChange={(e) => setDaughterTissueVolUnitOs(e.target.value)}
                                        aria-label="Unit"
                                        disabled={currentDaughterTissueIndex !== undefined && !daughterTissueOs}
                                    >
                                        {volumeUnitOptions.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        <Col xxl={6} className="mb-2">
                            <Form.Check
                                className="mb-2"
                                label="OD"
                                id="daughterTissueOd"
                                type="checkbox"
                                checked={daughterTissueOd}
                                onChange={() => setDaughterTissueOd(!daughterTissueOd)}
                                disabled={currentDaughterTissueIndex !== undefined}
                            />

                            <Form.Group className="mb-2">
                                <Form.Label>Date/Time of Preservation:</Form.Label>
                                <Form.Control disabled value={`${preservationDate} ${preservationTimezone}`} />
                            </Form.Group>
                            <Form.Group className="mb-2">
                                <Form.Label>Procured By:</Form.Label>
                                <Form.Control disabled value={mostRecentSignature?.userName ?? ""} />
                            </Form.Group>
                            <Form.Group controlId="odVolumeUnit">
                                <Form.Label>Volume</Form.Label>
                                <InputGroup>
                                    <Form.Control
                                        type="number"
                                        value={daughterTissueVolOd}
                                        min={0}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const regex = /^\d+(\.\d{0,2})?$/;
                                            if (regex.test(value)) {
                                                setDaughterTissueVolOd(+value);
                                            }
                                        }}
                                        disabled={currentDaughterTissueIndex !== undefined && !daughterTissueOd}
                                    />
                                    <Form.Select
                                        value={daughterTissueVolUnitOd}
                                        onChange={(e) => setDaughterTissueVolUnitOd(e.target.value)}
                                        aria-label="Unit"
                                        disabled={currentDaughterTissueIndex !== undefined && !daughterTissueOd}
                                    >
                                        {volumeUnitOptions.map((o) => (
                                            <option key={o.value} value={o.value}>
                                                {o.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </InputGroup>
                            </Form.Group>
                        </Col>
                        {isDuplicateSample && (
                            <Col xxl={12} className="mt-1">
                                <Alert variant="danger" className="hide-print d-flex justify-content-between">
                                    This daughter tissue has already been recovered.
                                </Alert>
                            </Col>
                        )}
                    </Row>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="secondary" onClick={handleCloseDaughterTissueModal}>
                        Close
                    </Button>
                    <div>
                        {!tissueCreated && currentDaughterTissueIndex !== undefined && (
                            <Button
                                variant="danger"
                                onClick={() => removeDaughterTissue(currentDaughterTissueIndex)}
                                className="me-2"
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            variant="primary"
                            onClick={handleSaveDaughterTissue}
                            disabled={!daughterTissueOs && !daughterTissueOd}
                        >
                            Save
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
}
